import { AlertTriangle, BarChart3, CheckCircle2, Clock, Database, Eye, TrendingUp, X, XCircle } from "lucide-react"
import { useState } from "react"

import { trpc } from "../../lib/trpc"

export default function ExtractionStatsTab() {
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)
  const { data: overallStats, isLoading: statsLoading } = trpc.extraction.getOverallStats.useQuery()
  const { data: templates, isLoading: templatesLoading } = trpc.extraction.listTemplates.useQuery()
  const { data: recentJobs } = trpc.extraction.listExtractionJobs.useQuery({ limit: 10 })

  if (statsLoading || templatesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent"></div>
          <span className="text-primary">Loading statistics...</span>
        </div>
      </div>
    )
  }

  const stats = overallStats || {
    totalJobs: 0,
    totalExtractedData: 0,
    jobStatusCounts: {},
    dataStatusCounts: {},
    templateCounts: {},
  }

  // Calculate success rate
  const completedJobs = stats.jobStatusCounts.completed || 0
  const failedJobs = stats.jobStatusCounts.failed || 0
  const totalFinishedJobs = completedJobs + failedJobs
  const successRate = totalFinishedJobs > 0 ? Math.round((completedJobs / totalFinishedJobs) * 100) : 0

  // Calculate approval rate
  const approvedData = stats.dataStatusCounts.approved || 0
  const rejectedData = stats.dataStatusCounts.rejected || 0
  const totalReviewedData = approvedData + rejectedData
  const approvalRate = totalReviewedData > 0 ? Math.round((approvedData / totalReviewedData) * 100) : 0

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold  mb-2">Extraction Statistics</h2>
        <p className="text-sm text-primary">Overview of your AI extraction system performance</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BarChart3 className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Jobs</p>
              <p className="text-2xl font-semibold ">{stats.totalJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Database className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Extracted Items</p>
              <p className="text-2xl font-semibold ">{stats.totalExtractedData}</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Success Rate</p>
              <p className="text-2xl font-semibold ">{successRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Approval Rate</p>
              <p className="text-2xl font-semibold ">{approvalRate}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Job Status Breakdown */}
        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <h3 className="text-lg font-medium  mb-4">Job Status Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(stats.jobStatusCounts).map(([status, count]) => {
              const statusIcons = {
                pending: <Clock className="w-4 h-4 text-gray-500" />,
                queued: <Clock className="w-4 h-4 text-yellow-500" />,
                running: <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse" />,
                completed: <CheckCircle2 className="w-4 h-4 text-green-500" />,
                failed: <XCircle className="w-4 h-4 text-destructive" />,
              }

              const statusColors = {
                pending: "text-primary",
                queued: "text-yellow-600",
                running: "text-blue-600",
                completed: "text-green-600",
                failed: "text-destructive",
              }

              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {statusIcons[status as keyof typeof statusIcons]}
                    <span
                      className={`text-sm font-medium capitalize ${
                        statusColors[status as keyof typeof statusColors] || "text-primary"
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                  <span className="text-sm  font-semibold">{count}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Data Status Breakdown */}
        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <h3 className="text-lg font-medium  mb-4">Data Status Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(stats.dataStatusCounts).map(([status, count]) => {
              const statusIcons = {
                pending: <Clock className="w-4 h-4 text-yellow-500" />,
                approved: <CheckCircle2 className="w-4 h-4 text-green-500" />,
                rejected: <XCircle className="w-4 h-4 text-destructive" />,
                processed: <CheckCircle2 className="w-4 h-4 text-blue-500" />,
                synced: <CheckCircle2 className="w-4 h-4 text-purple-500" />,
              }

              const statusColors = {
                pending: "text-yellow-600",
                approved: "text-green-600",
                rejected: "text-destructive",
                processed: "text-blue-600",
                synced: "text-purple-600",
              }

              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {statusIcons[status as keyof typeof statusIcons]}
                    <span
                      className={`text-sm font-medium capitalize ${
                        statusColors[status as keyof typeof statusColors] || "text-primary"
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                  <span className="text-sm  font-semibold">{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Template Usage */}
        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <h3 className="text-lg font-medium  mb-4">Template Usage</h3>
          <div className="space-y-3">
            {Object.entries(stats.templateCounts).length > 0 ? (
              Object.entries(stats.templateCounts).map(([templateId, count]) => (
                <div key={templateId} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium ">{templateId}</span>
                  </div>
                  <span className="text-sm text-primary">{count} extractions</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No template usage data yet</p>
            )}
          </div>
        </div>

        {/* Available Templates */}
        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <h3 className="text-lg font-medium  mb-4">Available Templates</h3>
          <div className="space-y-3">
            {templates && templates.length > 0 ? (
              templates.map((template: any) => (
                <div
                  key={template.id}
                  className="border border-border rounded-md p-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium  group-hover:text-blue-900">{template.name}</h4>
                      <p className="text-xs text-gray-500 mt-1 group-hover:text-blue-700">{template.description}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                      <span className="text-xs text-gray-500">v{template.version}</span>
                      {template.enabled ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-1">
                      {template.platforms.map((platform: string) => (
                        <span
                          key={platform}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-foregroundx text-gray-800 group-hover:bg-blue-100 group-hover:text-blue-800"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No templates available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Jobs */}
      {recentJobs && recentJobs.length > 0 && (
        <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
          <h3 className="text-lg font-medium  mb-4">Recent Extraction Jobs</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentJobs.slice(0, 5).map((job: any) => (
                  <tr key={job.id}>
                    <td className="px-4 py-3 text-sm font-mono ">{job.id.slice(0, 8)}...</td>
                    <td className="px-4 py-3 text-sm ">{job.templateId}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : job.status === "running"
                              ? "bg-blue-100 text-blue-800"
                              : job.status === "failed"
                                ? "bg-red-100 text-destructive"
                                : "bg-foregroundx text-gray-800"
                        }`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm ">{job.progress || 0}%</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(job.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Template Details Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold ">{selectedTemplate.name}</h2>
                <p className="text-sm text-gray-500 mt-1">Template Details</p>
              </div>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-gray-400 hover:text-primary transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium  mb-2">Template ID</h3>
                  <p className="text-sm text-primary font-mono bg-foregroundx rounded px-2 py-1">
                    {selectedTemplate.id}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium  mb-2">Version</h3>
                  <p className="text-sm text-primary">{selectedTemplate.version}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-medium  mb-2">Description</h3>
                <p className="text-sm text-primary">{selectedTemplate.description}</p>
              </div>

              {/* Status & Platforms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium  mb-2">Status</h3>
                  <div className="flex items-center space-x-2">
                    {selectedTemplate.enabled ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">Enabled</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-yellow-600 font-medium">Disabled</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium  mb-2">Supported Platforms</h3>
                  <div className="flex flex-wrap gap-1">
                    {selectedTemplate.platforms?.map((platform: string) => (
                      <span
                        key={platform}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Schema */}
              {selectedTemplate.schema && (
                <div>
                  <h3 className="text-sm font-medium  mb-2">Schema</h3>
                  <div className="bg-foregroundx rounded-lg p-4 overflow-x-auto">
                    <pre className="text-xs text-primary whitespace-pre-wrap">
                      {JSON.stringify(selectedTemplate.schema, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Extraction Instructions */}
              {selectedTemplate.extractionInstructions && (
                <div>
                  <h3 className="text-sm font-medium  mb-2">Extraction Instructions</h3>
                  <div className="bg-foregroundx rounded-lg p-4">
                    <p className="text-sm text-primary whitespace-pre-wrap">
                      {selectedTemplate.extractionInstructions}
                    </p>
                  </div>
                </div>
              )}

              {/* Post Processors */}
              {selectedTemplate.postProcessors && selectedTemplate.postProcessors.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium  mb-2">Post Processors</h3>
                  <div className="space-y-2">
                    {selectedTemplate.postProcessors.map((processor: any, index: number) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className=" font-medium">{processor.type}</span>
                        {processor.config && (
                          <span className="text-gray-500">- {JSON.stringify(processor.config)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Template JSON (collapsed by default) */}
              <details className="border border-border rounded-lg">
                <summary className="p-3 cursor-pointer text-sm font-medium  ">View Full Template JSON</summary>
                <div className="p-4 border-t border-border bg-foregroundx">
                  <pre className="text-xs text-primary whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(selectedTemplate, null, 2)}
                  </pre>
                </div>
              </details>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-border bg-foregroundx">
              <button
                onClick={() => setSelectedTemplate(null)}
                className="px-4 py-2 text-sm font-medium text-primary bg-background border border-gray-300 rounded-md  focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
